import { useRef } from "react"
import type {
    StatsReport,
    InboundVideoStatsReport,
    OutboundVideoStatsReport,
    OutboundAudioStatsReport,
    InboundAudioStatsReport,
} from "voximplant-websdk/Stats/v5/StatsReport"

import { StreamType } from "modules/Call/common/constants"

type Stream =
    | OutboundAudioStatsReport
    | OutboundVideoStatsReport
    | InboundAudioStatsReport
    | InboundVideoStatsReport

interface ConnectionStatus {
    isMyConnectionBad: boolean
    isPeerConnectionBad: boolean
}

const PACKET_LOSS_PERCENT = 15
const FRAMES_PER_SECOND = 3
const TIME_TO_UPDATE = 10000

const isVideoStream = (
    stream: Stream
): stream is InboundVideoStatsReport | OutboundVideoStatsReport => stream.kind === StreamType.VIDEO

const isConnectionBad = (stream: Stream, isOutbound: boolean): boolean => {
    const outboundStream = isOutbound ? (stream as OutboundVideoStatsReport) : null

    if (stream.loss > PACKET_LOSS_PERCENT) {
        return true
    }

    return !!(
        isVideoStream(stream) &&
        ((stream.framesPerSecond && stream.framesPerSecond < FRAMES_PER_SECOND) ||
            (isOutbound && outboundStream && outboundStream.qualityLimitationReason !== "none"))
    )
}

export const useDelayedConnectionAnalyzer = () => {
    const myBadSinceRef = useRef<number | null>(null)

    return (stats: StatsReport): ConnectionStatus => {
        const now = Date.now()
        let currentMyBad = false
        let currentPeerBad = false

        Object.values(stats.outbound).forEach((streams) => {
            streams.forEach((stream) => {
                if (isConnectionBad(stream, true)) {
                    currentMyBad = true
                }
            })
        })

        Object.values(stats.inbound).forEach((stream) => {
            if (isConnectionBad(stream, false)) {
                currentPeerBad = true
            }
        })

        if (currentMyBad && !myBadSinceRef.current) {
            myBadSinceRef.current = now
        }

        const isMyConnectionBad =
            !!myBadSinceRef.current && currentMyBad && now - myBadSinceRef.current >= TIME_TO_UPDATE

        if (!currentMyBad) {
            myBadSinceRef.current = null
        }

        return {
            isMyConnectionBad,
            isPeerConnectionBad: currentPeerBad,
        }
    }
}
